<script setup lang="ts">
/**
 * ScholarTrip 2.0 - 全局核心入口 (Production-Ready)
 * * 核心職責：
 * 1. AEO (Answer Engine Optimization) 結構化數據注入
 * 2. 全局身份驗證攔截 (Student Gating)
 * 3. 跨時區絕對時間初始化 (Dayjs UTC)
 * 4. 頂級 UI 轉場與載入體驗
 */
// 1. 引入核心 Store (預期路徑，稍後將在 Store 重構中補齊)
// import { useUserStore } from '~/stores/user/useUserStore'
// 模擬 UserStore 狀態 (確保目前程式碼可運行，待 Store 上傳後會自動對接)
const userStore = {
  isVerificationModalOpen: ref(false),
  closeVerification: () => {
    userStore.isVerificationModalOpen.value = false;
  },
};
// 2. AEO / SEO 策略：注入 JSON-LD 結構化資料
useHead({
  script: [
    {
      type: "application/ld+json",
      textContent: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "ScholarTrip",
        url: "https://scholartrip.com",
        potentialAction: {
          "@type": "SearchAction",
          target:
            "https://scholartrip.com/flights/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
        description:
          "Global student travel platform with AI-powered fare prediction.",
        offers: {
          "@type": "Offer",
          eligibleCustomerType: "Student",
          category: "Travel",
        },
      }),
    },
  ],
});
// 3. 全局滾動管理：確保頁面切換時自動回到頂部
const router = useRouter();
router.afterEach(() => {
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
});
</script>
<template>
  <div
    class="min-h-screen bg-slate-50 font-sans text-slate-900 antialiased selection:bg-brand-100 selection:text-brand-700"
  >
    <!-- A. 全局載入進度條：Google Flights 風格的高質感細線 -->
    <NuxtLoadingIndicator
      :color="'#3b82f6'"
      :height="3"
      :duration="2000"
      :throttle="200"
    />
    <!-- B. 主佈局容器 -->
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <!-- C. 全局組件層 (Global Overlay Layer) -->
    <!--
注意：StudentVerificationModal 必須放在此處以確保 Z-index 覆蓋全站。
後續將在 components/auth/ 下產出完整程式碼。
-->
    <ClientOnly>
      <div
        v-if="userStore.isVerificationModalOpen.value"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <!-- 遮罩層 -->
        <div
          class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
          @click="userStore.closeVerification"
        />
        <!-- 彈窗內容容器 (這裡將由真正的元件替換) -->
        <div
          class="relative w-full max-w-md transform overflow-hidden rounded-3xl bg-white shadow-2xl transition-all"
        >
          <!-- TODO: 此處將動態掛載 StudentVerificationModal 元件 -->
          <div class="p-8 text-center">
            <div
              class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50"
            >
              <span class="text-2xl">🎓</span>
            </div>
            <h3 class="text-xl font-extrabold text-slate-900">
              驗證您的學 生身份
            </h3>
            <p class="mt-2 text-slate-500">解鎖專屬學生特價與彈性機票條款。</p>
            <button
              @click="userStore.closeVerification"
              class="mt-6 w-full rounded-xl bg-brand-600 py-3 font-semibold text-white shadow-lg shadow-brand-200 transition-all hover:bg-brand-700 active:scale-[0.98]"
            >
              立即驗證
            </button>
          </div>
        </div>
      </div>
    </ClientOnly>
    <!-- 全局通知組件 (用於顯示錯誤或成功訊息) -->
    <div
      id="global-notifications"
      class="pointer-events-none fixed bottom-safe right-0 z-[100] p-6"
    />
  </div>
</template>
<style>
/* 1. 全局排版優化：
- 確保在所有平台上字體渲染一致 (抗鋸齒)
- 處理行動版 Safe Area 避免底部遮擋
*/
html {
  -webkit-tap-highlight-color: transparent;
  scroll-behavior: smooth;
  height: 100%;
}
body {
  margin: 0;
  padding: 0;
  min-height: 100%;
}
/* 2. 頁面過渡動畫：對標 Google Flights 的流暢感 (Slide + Fade) */
.page-enter-active,
.page-leave-active {
  transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.page-enter-from {
  opacity: 0;
  transform: translateX(15px);
}
.page-leave-to {
  opacity: 0;
  transform: translateX(-15px);
}
/* 3. 滾動條美化 (Chrome/Safari) */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}
/* 4. 針對行動版 Safe Area 的適配 */
.bottom-safe {
  bottom: env(safe-area-inset-bottom, 1rem);
}
</style>
